import { useRef, useState } from "react";
import { Check } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { InlineEditShell } from "./InlineEditShell";
import { useInlineEdit } from "./useInlineEdit";

export type InlineOption<T> = {
  value: T;
  label: string;
  /** Conteúdo rico da linha da lista; cai no `label` quando ausente. */
  node?: React.ReactNode;
  /** Termos extras de busca (ex.: e-mail do vendedor). */
  keywords?: string;
};

/**
 * Escolha de um valor numa lista, por clique. Serve estágio, origem e
 * responsável — o que muda entre eles é só a lista de opções.
 *
 * Com poucas opções o campo de busca some: digitar para filtrar 3 estágios
 * seria atrito, não ajuda.
 */
export default function InlineEditSelect<T extends string | number | null>({
  value,
  options,
  onCommit,
  canEdit,
  label,
  children,
  className,
  align = "left",
  searchPlaceholder = "Buscar...",
  emptyMessage = "Nada encontrado",
  searchThreshold = 7,
  loading = false,
  disabledReason,
  fill = false,
}: {
  value: T;
  options: InlineOption<T>[];
  /** `option` acompanha para quem precisa do rótulo escolhido no otimista. */
  onCommit: (next: T, option?: InlineOption<T>) => Promise<void>;
  canEdit: boolean;
  label: string;
  children: React.ReactNode;
  className?: string;
  align?: "left" | "right";
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** A partir de quantas opções o campo de busca aparece. */
  searchThreshold?: number;
  loading?: boolean;
  disabledReason?: string;
  /** Ocupa a largura do container em vez de se ajustar ao conteúdo — ver o
   *  `fill` do InlineEditShell. Precisa valer também para o <button>: ele é
   *  `inline-block`, e um segundo box dimensionado pelo conteúdo no caminho
   *  anula o efeito de esticar só a casca. */
  fill?: boolean;
}) {
  const [open, setOpen] = useState(false);

  // O rótulo da última opção escolhida, para repassar ao onCommit.
  const pickedRef = useRef<InlineOption<T> | undefined>(undefined);

  const edit = useInlineEdit<T>({
    value,
    onCommit: (next) => onCommit(next, pickedRef.current),
    disabled: !canEdit,
  });

  const showSearch = options.length >= searchThreshold;

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        if (!canEdit) return;
        setOpen(v);
        if (v) edit.start();
        else edit.cancel();
      }}
    >
      <PopoverTrigger asChild disabled={!canEdit}>
        <button
          type="button"
          aria-label={`Editar ${label}`}
          className={cn(
            "max-w-full text-left disabled:cursor-default",
            fill && "w-full",
          )}
          disabled={!canEdit}
          onClick={(e) => e.stopPropagation()}
        >
          <InlineEditShell
            canEdit={canEdit}
            status={edit.status}
            error={edit.error}
            label={label}
            triggerProps={{}}
            interactive={false}
            className={className}
            align={align}
            disabledReason={disabledReason}
            fill={fill}
          >
            {children}
          </InlineEditShell>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[260px] p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <Command>
          {showSearch && <CommandInput placeholder={searchPlaceholder} />}
          <CommandList>
            {loading ? (
              <div className="py-4 text-center text-xs text-muted-foreground">
                Carregando...
              </div>
            ) : (
              <>
                <CommandEmpty>{emptyMessage}</CommandEmpty>
                <CommandGroup>
                  {options.map((opt) => (
                    <CommandItem
                      key={String(opt.value)}
                      value={`${opt.label} ${opt.keywords ?? ""}`}
                      onSelect={() => {
                        setOpen(false);
                        pickedRef.current = opt;
                        void edit.commit(opt.value);
                      }}
                      className="gap-2"
                    >
                      <Check
                        className={cn(
                          "h-3.5 w-3.5 shrink-0",
                          opt.value === value ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="min-w-0 flex-1">{opt.node ?? opt.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
